import dynamic from "next/dynamic";
import Head from "next/head";
import Theme from "../components/Theme";
import AdaptiveCriticalImage from "@components/atoms/AdaptiveCriticalImage";
import CutoutFeaturedImage from "@components/organisms/common/CutoutFeaturedImage";
import PremiumPlanComparision from "@components/organisms/pricing/PremiumPlanComparision";
import AppStorage from "../components/AppStorage";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../styles/cssinjs/helpers/variables.js";

const InjectChatSupport = dynamic(() =>
  import("@components/atoms/InjectChatSupport")
);
const VideoSchema = dynamic(() =>
  import("@components/organisms/common/VideoSchema")
);
const FeaturesGrid = dynamic(() =>
  import("@components/organisms/common/FeaturesGrid")
);
const GetProFreePlan = dynamic(() =>
  import("@components/organisms/common/GetProFreePlan")
);
const WideCTA = dynamic(() => import("@components/organisms/common/WideCTA"));
export async function getStaticProps(ctx) {
  let allData = {};
  const { locale } = ctx;
  // Page Route
  const route = "pro";

  // Fetch data for respective pages
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );

  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );

  const pricingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/pricing.json`
  );

  const marketingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/pricing/${route}/marketing.json`
  );

  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/pricing/${route}/content.json`
  );

  // Catch Error
  if (
    !headerResponse.ok ||
    !footerResponse.ok ||
    !marketingResponse.ok ||
    !contentResponse.ok
  ) {
    return {
      notFound: true,
    };
  }

  // Convert
  allData.header = await headerResponse.json();
  allData.footer = await footerResponse.json();
  allData.marketing = await marketingResponse.json();
  allData.content = await contentResponse.json();
  allData.pricing = await pricingResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };
  const pricing = {
    pricing: allData.pricing,
  };

  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...pricing,
    ...allData.marketing,
    ...allData.content,
  };

  // Return to the component
  return {
    props: {
      data,
      locale,
    },
  };
}

export const FeaturesGridSection = ({ data }) => {
  const appMode = process.env.APP_MODE || "staging";
  return (
    <section className="bg-light-blue-grey lg:pt-4 lg:pb-10 md:pb-10">
      <FeaturesGrid data={data.list} colCount={data.colCount} />
      <div className="premium-bg-cta br-8 container cta-contrast has-image lg:d-fx lg:pt-10 lg:pb-10 lg:mb-10 lg:mt-10 md:mt-10 md:pl-0 md:pb-0">
        <article className="lg:maxw-lg lg:pr-10 md:pl-2.5">
          <h2 className="lg:mt-1 md:mb-2 text-black text-lg">
            {data.cta.title}
          </h2>
          <p className="lg:mb-5 lg:mt-3 lg:pr-3 md:mb-2.5 text-grey">
            {data.cta.desc}
          </p>
          <div className="lg:d-fx lg:mb-5 md:d-none">
            <figure className="fx icon-wrap">
              {data.cta?.icons
                ? data.cta?.icons?.map((item, index) => (
                    <img
                      key={index}
                      src={item.image}
                      alt={item.alt}
                      className="w-auto"
                      loading="lazy"
                      style={{ maxWidth: "160px", height: "24px" }}
                    />
                  ))
                : null}
              <style jsx>{`
                .icon-wrap {
                  gap: 32px;
                }
              `}</style>
            </figure>
          </div>
          <a
            href={
              typeof data?.cta?.button?.url === "string"
                ? data.cta.button.url
                : data?.cta?.button?.url?.[appMode]
            }
            className={`btn-primary btn-lg md:w-100 ${
              data?.cta?.button?.id ?? ""
            }`}
          >
            <span>{data?.cta?.button?.text}</span>
          </a>
        </article>
        <picture className="md:mt-5 md:pl-5">
          <AdaptiveCriticalImage
            src={data.cta.image.src}
            alt={data.cta.image.alt}
            mobile={data.cta.image.prefixes.mobile}
            dpi={data.cta.image.prefixes.dpi}
            className="lg:mb-1"
            dimensions={data.cta.image.dimensions}
          />
        </picture>
      </div>
    </section>
  );
};
export default function Pro({ data, locale }) {
  const { seo, header, footer, pricing, content } = data;

  const PricingPlanWithTitleDesc = ({ data }) => (
    <section className="container grid--default has-align-top hero--default pos-rel lg:pb-4 md:mb-7 md:pb-10">
      <article className="text-left md:mb-2.5">
        <h1
          className="lg:pt-5 text-xl"
          dangerouslySetInnerHTML={{ __html: data.title }}
        ></h1>
        <p className="lg:maxw-md lg:pr-4">{data.desc}</p>
        <div className="app-review-wrap is-adaptive lg:d-fx md:ml-0 md:mt-10">
          <div className="app-review getapp-review lg:mr-5">
            <figure className="lg:mr-1 md:mr-1">
              <img
                src={data.getapp.image}
                alt={data.getapp.alt}
                loading="lazy"
              />
            </figure>
            <div className="md:text-left">
              <p>{data.getapp.heading}</p>
              <span>{data.getapp.text}</span>
            </div>
          </div>
          <div className="app-review">
            <figure className="lg:mr-0">
              <img
                src={data.capterra.image}
                alt={data.capterra.alt}
                loading="lazy"
              />
            </figure>
            <div className="md:text-left">
              <p>{data.capterra.heading}</p>
              <span>{data.capterra.text}</span>
            </div>
          </div>
        </div>
      </article>
      <figure className="layered-images-wrap lg:maxw-lg">
        <picture>
          <AdaptiveCriticalImage
            src={data.images.src}
            alt={data.images.alt}
            mobile={data.images.prefixes.mobile}
            dpi={data.images.prefixes.dpi}
            dimensions={[375, 340]}
          />
        </picture>
      </figure>
      <div
        className="pricing-wrap pricing-wrap-mobile pricing-container"
        id={content.initialPlanType}
      >
        <PremiumPlanComparision
          toggle={content.pricingPlan.toggle}
          plans={content.pricingPlan.plans}
          planInfo={content.pricingPlan.planInfo}
          initialPlanType={content.initialPlanType}
          locale={locale}
        />
      </div>
      <style jsx>{`
        .layered-images-wrap {
          left: 45%;
        }
        .pricing-container {
          position: relative;
        }
        .setmore-emblem {
          display: none;
        }
        @media (max-width: ${viewport.desktop.sm}) {
          .layered-images-wrap{
            display: none;
        }
        @media (max-width: ${viewport.tablet.sm}) and (min-width: ${viewport.mobile.lg}) {
          .pricing-wrap {
            width: 100%;
            top: 54px;
            left: 100px;
            padding-bottom: 124px;
          }
          .pricing-wrap {
            left: 0px !important;
          }
          .layered-images-wrap {
            top: 0;
          }
          .container {
            padding-top: 0;
          }
        }
      `}</style>
    </section>
  );

  const SecondFold = ({ data }) => {
    return (
      <CutoutFeaturedImage
        data={data}
        className="lg:pb-15 md:pb-10 md:pt-5 bg-light-blue-grey"
      />
    );
  };

  const WindowLayoutLeft = ({ data }) => {
    return (
      <section className="window-layout bg-light-blue-grey lg:-mb-4 md:-mt-5">
        <div className="container grid--default has-align-top use-setmore-wrap">
          <picture className="lg:maxw-lg md:-mb-5">
            <img src={data.image.src} alt={data.image.alt} loading="lazy" />
          </picture>
          <article className="text-left lg:maxw-md">
            <h2
              className="text-lg lg:mt-9 lg:mb-3 md:mb-2 md:mt-5"
              dangerouslySetInnerHTML={{ __html: data.title }}
            />
            <p className="lg:mb-1 md:mb-5">{data.desc}</p>
          </article>
        </div>
      </section>
    );
  };
  const WindowLayoutRight = ({ data }) => {
    return (
      <section className="bg-white lg:pt-10">
        <div className="container grid--default has-align-top lg:pt-10 md:fx md:fx--w md:pt-10">
          <article className="text-left lg:maxw-md lg:mt-3 lg:pt-10 lg:mr-15">
            <h2
              className="text-lg lg:mb-3 md:mt-5 md:mb-2"
              dangerouslySetInnerHTML={{ __html: data.title }}
            />
            <p>{data.desc}</p>
            <a
              href={data.button.url}
              className="btn-primary lg:mt-5 md:mb-5 md:mt-5 md:w-100"
            >
              <span>{data.button.text}</span>
            </a>
          </article>
          <picture className="lg:maxw-lg custom-img-size lg:ml-2 lg:pt-4 md:w-100">
            <img src={data.image.src} alt={data.image.alt} loading="lazy" />
          </picture>
        </div>
      </section>
    );
  };
  return (
    <>
      <Head>
        <meta
          name="apple-itunes-app"
          content="app-id=665859578, app-argument=https://apps.apple.com/us/app/setmore-appointment-scheduling/id665859578?pid=ios_smart_banner&c=ios_campaign"
        />
      </Head>
      <Theme
        header={header}
        footer={footer}
        seo={seo}
        className="page--home industry-with-video premium-pro pricing-layout locale"
      >
        <AppStorage pageName="Pricing - pro" />
        <PricingPlanWithTitleDesc data={content.hero} />
        <SecondFold data={content.cutoutFeaturedImage} />
        <FeaturesGridSection data={content.featuresGrid} />
        <WindowLayoutLeft data={content.featuredImageSections[0]} />
        <WindowLayoutRight data={content.featuredImageSections[1]} />
        {pricing.pricingPlanContent ? (
          <section className="bg-light-blue-grey lg:mt-20 md:mt-5">
            <GetProFreePlan
              content={pricing.pricingPlanContent}
              pricing={pricing}
              initialPlanType="annual-billing-plan"
              className="pricing-wrap lg:pt-10"
            />
          </section>
        ) : null}
        <WideCTA data={content.wideCTA} />
        <InjectChatSupport />
        <style jsx global>{`
          .green-text {
            color: ${colors.primitive.green.light};
          }
          .chatsupport-button {
            visibility: visible !important;
          }
          .pricing-wrap.has-align-top .card--default > * {
            padding: 24px 48px !important;
          }
          .pricing-wrap.has-align-top
            .card--default
            > *:nth-child(2)
            .arrow-link,
          .pricing-wrap.has-align-top
            .card--default
            > *:nth-child(3)
            .arrow-link {
            display: none;
          }
          .pricing-wrap.has-align-top .card--default > * .plan-benefits {
            margin-bottom: 0px !important;
          }
          @media (max-width: ${viewport.tablet.sm}) {
            .premium-pro .use-setmore-wrap .video-highlight {
              max-width: fit-content;
            }
          }
          @media (min-width: ${viewport.desktop.sm}) {
            .premium-pro .use-setmore-wrap .video-highlight {
              border: 4px solid ${colors.primitive.black[200]};
              box-shadow: 0 1px 2px 0 rgb(48 58 71 / 20%),
                0 1px 3px 0 rgb(48 58 71 / 10%);
            }
            .premium-pro .use-setmore-wrap figure + article {
              margin-top: 30px !important;
            }
          }
          @media (max-width: ${viewport.mobile.md}) {
            .premium-pro .use-setmore-wrap figure {
              margin-top: -90px;
            }
            .pricing-wrap.has-align-top .card--default > *:nth-child(1) {
              padding-bottom: 55px !important;
            }
            .pricing-wrap.has-align-top .card--default > * {
              padding-right: 45px !important;
              padding-left: 45px !important;
            }
          }
        `}</style>
        <Head>
          <VideoSchema data={content.cutoutFeaturedImage} />
        </Head>
      </Theme>
    </>
  );
}
